import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { AuthContext } from '../App';
import PhotoTile from '../components/PhotoTile';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * PhotosPage - All Photos view for a user profile
 * 
 * Route: /profile/:userId/photos
 * 
 * Privacy Rules (matches current system):
 * - Owner: sees Private + Circle + Public
 * - Connected viewer: sees Circle + Public (if allowed), never Private
 * - Logged-out viewer: sees Public only (or empty state)
 */
export default function PhotosPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  
  const [photos, setPhotos] = useState([]);
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState('none');
  
  const isOwner = currentUser?.user_id === userId;

  useEffect(() => {
    fetchPhotos();
    if (isOwner) {
      fetchCircles();
    }
  }, [userId, isOwner]);

  const fetchPhotos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const endpoint = isOwner 
        ? `${API_URL}/api/photos`
        : `${API_URL}/api/photos/profile/${userId}`;
      
      const res = await fetch(endpoint, { headers });
      if (res.ok) {
        const data = await res.json();
        setPhotos(data.photos || []);
        setAccess(data.access || (isOwner ? 'owner' : 'none'));
      }
    } catch (err) {
      console.error('Error fetching photos:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCircles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/circles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCircles(Array.isArray(data) ? data : (data.circles || []));
      }
    } catch (err) {
      console.error('Error fetching circles:', err);
    }
  };

  const handlePhotoUpdate = (updatedPhoto) => {
    setPhotos(prev => prev.map(p => 
      (p.photo_id === updatedPhoto.photo_id || p.id === updatedPhoto.id) 
        ? updatedPhoto 
        : p
    ));
  };

  const handlePhotoDelete = (photoId) => {
    setPhotos(prev => prev.filter(p => p.photo_id !== photoId && p.id !== photoId));
  };

  // Filter photos with valid URLs
  const validPhotos = photos.filter(photo => photo.url && photo.url.trim() !== '');

  // Empty state messages (LOCKED COPY)
  const getEmptyMessage = () => {
    if (!currentUser) {
      return "No public photos yet.";
    }
    if (isOwner) {
      return "No photos yet.";
    }
    return "No photos shared with you yet.";
  };

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: 'var(--fy-background-primary)' }}
    >
      {/* Header */}
      <div 
        className="sticky top-0 z-10 border-b"
        style={{ 
          backgroundColor: 'var(--fy-background-primary)',
          borderColor: 'var(--fy-border-subtle)'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            style={{ color: 'var(--fy-text-primary)' }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 
              className="text-xl font-semibold"
              style={{ color: 'var(--fy-text-primary)' }}
            >
              Photos
            </h1>
            <p 
              className="text-sm"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              Shared with friends. Not public.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div 
                key={i}
                className="aspect-square rounded-lg animate-pulse"
                style={{ backgroundColor: 'var(--fy-surface-muted)' }}
              />
            ))}
          </div>
        ) : validPhotos.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {validPhotos.map((photo) => (
              <PhotoTile
                key={photo.photo_id || photo.id}
                photo={photo}
                isOwner={isOwner}
                circles={circles}
                onUpdate={handlePhotoUpdate}
                onDelete={handlePhotoDelete}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <ImageIcon 
              className="w-16 h-16 mx-auto mb-4"
              style={{ color: 'var(--fy-text-disabled)' }}
            />
            <p 
              className="text-lg"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              {getEmptyMessage()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
